import React from 'react';

import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Box, Flex, Stack } from '@atlaskit/primitives/compiled';

import { getFeatureInfo } from '../../../common/getFeatureInfo';
import type { AudioFeature } from '../../../types/audioFeature';
import { useAudioPlayerStreamingContext } from '../../context/AudioPlayerStreamingProvider';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { ModifyPlaylistButton } from '../playlist/ModifyPlaylistButtons';

import { AudioFeatureToggle } from './AudioFeatureToggle';
import { AudioHeader } from './AudioHeader';
import { AudioShareLinkButton } from './AudioShareLinkButton';

export type AudioHeaderPanelProps = {
	featureList: AudioFeature[];
	cloudId: string;
};

const styles = cssMap({
	cardWrapper: {
		height: '100%',
	},
});

export const AudioHeaderPanel = ({ featureList }: AudioHeaderPanelProps): React.JSX.Element => {
	const { activeFeature, setActiveFeature, cloudId } = useAudioPlayerStore();
	const { fetchAudio } = useAudioPlayerStreamingContext();

	const activeFeatureInfo = getFeatureInfo(activeFeature, featureList);

	const getNextFeature = () => {
		const currentIndex = featureList.findIndex(
			(feature) => feature.name === activeFeatureInfo?.name,
		);
		const nextIndex = (currentIndex + 1) % featureList.length;
		return featureList[nextIndex];
	};
	const { pendingContent, activeContent } = useAudioPlayerStore();

	const handleFeatureToggle = () => {
		const nextFeature = getNextFeature().name;
		setActiveFeature(nextFeature);
		fetchAudio(nextFeature);
	};

	if (FeatureGates.getExperimentValue('confluence_audio_v3_changeboarding', 'isEnabled', true)) {
		return (
			<Stack space="space.250" alignInline="center">
				<AudioHeader featureList={featureList} />
				<Flex gap="space.050" alignItems="center">
					<Box id="audio-player-changeboarding-step-1" xcss={styles.cardWrapper}>
						<ModifyPlaylistButton
							round={true}
							cloudId={cloudId}
							contentId={pendingContent?.contentId || activeContent.contentId}
							contentType={pendingContent?.contentType || activeContent.contentType}
							title={pendingContent?.title || activeContent.title}
							// @ts-ignore breifing and narration are tricky to type across the app
							trackType={activeFeature?.toUpperCase()}
						/>
					</Box>
					<AudioFeatureToggle featureList={featureList} onClick={handleFeatureToggle} />
					<AudioShareLinkButton />
				</Flex>
			</Stack>
		);
	}

	return (
		<Stack space="space.250" alignInline="center">
			<AudioHeader featureList={featureList} />
			<Flex gap="space.050" alignItems="center">
				<ModifyPlaylistButton
					round={true}
					cloudId={cloudId}
					contentId={pendingContent?.contentId || activeContent.contentId}
					contentType={pendingContent?.contentType || activeContent.contentType}
					title={pendingContent?.title || activeContent.title}
					// @ts-ignore breifing and narration are tricky to type across the app
					trackType={activeFeature?.toUpperCase()}
					actionSource="audioPlayer"
				/>
				<AudioFeatureToggle featureList={featureList} onClick={handleFeatureToggle} />
				<AudioShareLinkButton />
			</Flex>
		</Stack>
	);
};
